/**
 * useChatSocket - Socket.IO Hook para mensagens em tempo real
 * Gerencia eventos do socket para conversas e mensagens
 */

import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import type {
  ConversationMessageResponse,
  MessageDeletedEvent,
  MessageEditedEvent,
  MessageReactionEvent,
  MessageStatusEvent,
  MessageTranscribedEvent
} from '@troia-v3/types';
import { SOCKET_EVENTS as Events } from '@troia-v3/types';
import type { ComposerState } from './useMessagingWindow';

/** Forma mínima do item no cache `['messages','infinite', id]`. */
interface CachedMessage {
  id?: string;
  isOptimistic?: boolean;
  status?: string;
}

interface MessagesPage {
  items: CachedMessage[];
}

interface MessagesInfiniteData {
  pages: MessagesPage[];
}

/** Balão otimista (`temp-…`) cujo POST ainda não voltou. */
const isPendingOptimistic = (item: CachedMessage): boolean =>
  item.id?.startsWith('temp-') === true && item.isOptimistic === true && item.status !== 'failed';

/**
 * A página é newest-first: o otimista pendente mais ANTIGO é o de maior índice.
 * Envios são sequenciais, então o eco que chega agora é dele.
 */
const findOldestPendingOptimisticIndex = (items: CachedMessage[]): number => {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (item && isPendingOptimistic(item)) return i;
  }
  return -1;
};

/**
 * Eco do MEU envio: outbound, mandado por usuário, e o usuário sou eu. Payload
 * sem `sender` conta como meu — o pior caso é trocar o balão otimista pelo
 * envio de um colega, e a resposta HTTP do meu POST recoloca o meu
 * (`updateOptimisticMessage`, caso 4). Nada se perde em nenhum ramo.
 */
const isEchoOfMyPendingSend = (
  message: Partial<ConversationMessageResponse>,
  currentUserId: string | undefined,
): boolean => {
  if (message.direction !== 'outbound') return false;
  const senderType = message.senderType ?? message.sender?.type;
  if (senderType !== 'user') return false;
  const senderId = message.sender?.id;
  return !senderId || !currentUserId || senderId === currentUserId;
};

const isMessageInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: readonly unknown[],
  messageId: string,
): boolean =>
  queryClient
    .getQueriesData<MessagesInfiniteData>({ queryKey, exact: false })
    .some(([, data]) => data?.pages.some((page) => page.items.some((item) => item.id === messageId)));

interface UseChatSocketOptions {
  /** ID da conversa para filtrar mensagens */
  conversationId?: string;
  /**
   * Estado do composer (fonte única: `useMessagingWindow`, calculado em
   * `ChatBox.tsx`). B10 (review 2026-07-27): usado SÓ para decidir se um
   * inbound invalida `['conversation', id]` — não recalcular a janela aqui,
   * é ela quem decide se a mensagem entra no cache (isso continua incondicional).
   */
  composerState?: ComposerState;
  /** Callback quando nova mensagem é recebida */
  onNewMessage?: (message: ConversationMessageResponse) => void;
  /** Callback quando mensagem é atualizada (editada, status mudou) */
  onMessageUpdate?: (message: ConversationMessageResponse) => void;
  /** Callback quando mensagem é deletada */
  onMessageDelete?: (messageId: string) => void;
  /** Callback quando conversa é atualizada */
  onConversationUpdate?: (conversationId: string) => void;
  /** Habilitar/desabilitar socket */
  enabled?: boolean;
}

/**
 * Hook para gerenciar eventos Socket.IO em tempo real
 *
 * @example
 * ```tsx
 * const { isConnected } = useChatSocket({
 *   conversationId: '123',
 *   onNewMessage: (msg) => console.log('Nova mensagem:', msg),
 * });
 * ```
 */
export const useChatSocket = (options: UseChatSocketOptions = {}) => {
  const {
    conversationId,
    composerState,
    onNewMessage,
    // onMessageUpdate,
    onMessageDelete,
    // onConversationUpdate,
    enabled = true,
  } = options;

  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();
  const { state: authState } = useAuth();
  const currentUserId = authState.user?.id;

  // ✅ Deduplicação: armazena IDs de mensagens recebidas recentemente
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // B10 (review 2026-07-27): `composerState` sempre fresco dentro do handler do
  // socket sem precisar recriar `handleMessageReceived` (e portanto sem precisar
  // re-registrar o listener) a cada mudança de estado do composer. Atribuição
  // direta no corpo do componente (não em `useEffect`) — roda em toda render,
  // então no instante em que o evento de socket dispara, `.current` já reflete
  // o `composerState` do render mais recente, nunca uma closure presa num
  // render antigo (o bug que a nota do dispatch pediu pra evitar).
  const composerStateRef = useRef(composerState);
  composerStateRef.current = composerState;

  /**
   * Adiciona mensagem ao cache da conversa atual (`['messages', 'infinite', ...]`).
   *
   * A atualização do cache da LISTA de conversas (`['conversations', ...]`)
   * foi movida para `useConversationListSocket` (montado globalmente via
   * `GlobalSocketListeners`) — assim a sidebar reage mesmo quando o usuário
   * não está na rota de chat. Ver DOCS/modules/CHAT_NOTIFICATIONS.md.
   */
  const handleMessageReceived = useCallback((message: Partial<ConversationMessageResponse>, eventConversationId: string) => {
    if (!message.id) return;

    // Só atualiza cache de mensagens quando este hook está vinculado a uma
    // conversa específica (ChatBox). Sidebar (sem conversationId) só usa este
    // hook pra callbacks — a lista em si atualiza via hook global.
    if (!conversationId || conversationId !== eventConversationId) return;

    // Mensagem do cliente reabre a janela de 24h — refetch da conversa (o
    // `staleTime` de 30s do useConversation não pegaria a mudança a tempo).
    //
    // B10 (review 2026-07-27, spec §5.11): condicionado a inbound E a
    // `composerState === 'window_closed'` — antes só checava `direction`, e
    // TODO inbound (inclusive em `gateway-whatsapp`, widget, e-mail, Telegram,
    // onde `messagingWindow`/janela nem existe) disparava um
    // `GET /conversations/:id` (aggregation com múltiplos `$lookup`) a cada
    // mensagem numa conversa ativa de alto volume. `composerStateRef.current`
    // (não a variável `composerState` capturada no closure deste `useCallback`)
    // porque só o ref garante o valor do render MAIS RECENTE no instante em
    // que o evento chega — se lesse a variável direto, `handleMessageReceived`
    // precisaria de `composerState` nas deps do `useCallback`, e mesmo assim o
    // `socket.on` no `useEffect` abaixo só re-liga o listener quando o efeito
    // roda de novo; entre um evento e o próximo render, o valor capturado
    // no closure poderia estar 1 estado atrás — janela reabrindo na tela
    // depende de acertar exatamente este instante, então o ref é o único jeito
    // de não arriscar reintroduzir esse bug (pior que o excesso de GETs).
    if (message.direction === 'inbound' && composerStateRef.current === 'window_closed') {
      queryClient.invalidateQueries({ queryKey: ['conversation', eventConversationId] });
    }

    const messagesQueryKey = ['messages', 'infinite', eventConversationId];
    const messageId = message.id;

    // Há busca em voo (refetch ao voltar do background, invalidação, página
    // antiga carregando)? A resposta dela foi gerada ANTES desta mensagem
    // existir e, ao aterrissar, substitui o cache inteiro — apagando a inserção
    // abaixo. Lido ANTES de mexer no cache. Incidente 2026-08-29: "toast e
    // contador atualizam, o balão não aparece" — ver `useChatSocket.test.tsx`
    // (cenários 1 e 4) e `tests/browser/chat-background-messages.spec.ts`.
    const hadFetchInFlight = queryClient.isFetching({ queryKey: messagesQueryKey, exact: false }) > 0;

    queryClient.setQueriesData<MessagesInfiniteData>(
      { queryKey: messagesQueryKey, exact: false },
      (oldData) => {
        if (!oldData?.pages) return oldData;

        const messageExists = oldData.pages.some((page) =>
          page.items.some((item) => item.id === messageId)
        );
        if (messageExists) return oldData;

        const firstPage = oldData.pages[0];
        if (!firstPage) return oldData;

        // Eco do MEU envio: o socket costuma entregar o eco ANTES da resposta
        // HTTP do POST. Substitui o balão otimista no lugar — nunca descarta.
        // Antes, a guarda descartava QUALQUER mensagem enquanto houvesse
        // otimista pendente, inclusive a resposta do cliente que chegava nos
        // segundos do envio (`useChatSocket.test.tsx`, cenários 2 e 3).
        // B9 (review 2026-07-27): otimista com status:'failed' não conta —
        // `markOptimisticMessageFailed` mantém `isOptimistic`/id `temp-`.
        const echoIndex = isEchoOfMyPendingSend(message, currentUserId)
          ? findOldestPendingOptimisticIndex(firstPage.items)
          : -1;

        const items: CachedMessage[] = echoIndex >= 0
          ? firstPage.items.map((item, index) =>
              index === echoIndex ? { ...item, ...message, isOptimistic: false } : item
            )
          : [message as CachedMessage, ...firstPage.items];

        const newPages = [...oldData.pages];
        newPages[0] = { ...firstPage, items };
        return { ...oldData, pages: newPages };
      }
    );

    if (hadFetchInFlight) {
      // Cancela a resposta velha e refaz a busca — a nova já traz esta
      // mensagem; a inserção acima mantém o balão na tela nesse meio-tempo.
      void queryClient
        .invalidateQueries({ queryKey: messagesQueryKey, exact: false })
        .then(() => {
          // A carga INICIAL ignora o cancelamento (não há dado a preservar) e a
          // resposta dela pode ter nascido antes desta mensagem. Confere e
          // refaz UMA vez — nunca em laço (mensagem apagada no servidor não
          // pode virar refetch eterno).
          if (!isMessageInCache(queryClient, messagesQueryKey, messageId)) {
            void queryClient.invalidateQueries({ queryKey: messagesQueryKey, exact: false });
          }
        });
    }
  }, [queryClient, conversationId, currentUserId]);

  /**
   * Marca mensagens como lidas via Socket.IO
   */
  const sendReadReceipt = useCallback((messageIds: string[]) => {
    if (!socket?.connected) {
      return;
    }

    if (!conversationId) {
      return;
    }

    socket.emit('read_receipt', {
      conversationId,
      messageIds,
    });

    // Atualiza status da mensagem no cache para 'read'
    queryClient.setQueriesData(
      {
        queryKey: ['messages', 'infinite', conversationId],
        exact: false
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((msg: any) =>
            messageIds.includes(msg.id)
              ? { ...msg, status: 'read', readAt: new Date().toISOString() }
              : msg
          ),
        }));

        return { ...oldData, pages: newPages };
      }
    );

    // Invalida contagem de não lidas após marcar como lida
    queryClient.invalidateQueries({
      queryKey: ['unread-count'],
    });
    queryClient.invalidateQueries({
      queryKey: ['conversations', 'unread-count'],
      exact: false,
    });
  }, [socket, conversationId, queryClient]);

  /**
   * ✅ NEW ARCHITECTURE: Receive complete ConversationMessageResponse from socket
   * Socket event now sends identical structure as API response - zero conversion needed!
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const hookId = `useChatSocket-${conversationId || 'no-id'}-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`🔌 [${hookId}] Registrando listener socket`);

    // ✅ SIMPLIFIED: Receive complete message object directly from backend
    const handleNewMessage = (message: ConversationMessageResponse) => {
      console.log(`📨 [${hookId}] Recebeu mensagem:`, message.id, 'direction:', message.direction);

      // ✅ DEDUPLICAÇÃO: Ignorar mensagens já processadas nos últimos 2 segundos
      if (processedMessagesRef.current.has(message.id)) {
        console.log(`⏭️ [${hookId}] Mensagem duplicada ignorada:`, message.id);
        return;
      }

      // Adicionar à lista de processadas
      processedMessagesRef.current.add(message.id);

      // Limpar cache após 2 segundos
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      cleanupTimeoutRef.current = setTimeout(() => {
        processedMessagesRef.current.delete(message.id);
      }, 2000);

      // Filtrar por conversationId se especificado
      const isCurrentConversation = conversationId && message.conversationId === conversationId;

      if (conversationId && !isCurrentConversation) {
        console.log(`⏭️ [${hookId}] Mensagem ignorada - conversationId diferente`, {
          expected: conversationId,
          received: message.conversationId
        });
        return;
      }

      // ✅ ZERO CONVERSION NEEDED - message is already in correct format!

      // Callback customizado (se fornecido)
      if (onNewMessage) {
        console.log(`🔔 [${hookId}] Executando callback onNewMessage`);
        onNewMessage(message);
      }

      // ✅ Adiciona mensagem ao cache e invalida queries relacionadas
      console.log(`💾 [${hookId}] Adicionando mensagem ao cache e invalidando queries`);
      handleMessageReceived(message, message.conversationId);

      // ✅ Se a conversa está aberta, marcar mensagem como lida automaticamente via Socket.IO
      if (isCurrentConversation) {
        console.log(`✅ [${hookId}] Marcando mensagem como lida automaticamente`);
        sendReadReceipt([message.id]);
      }
    };

    // ✅ Type-safe event listener usando constante compartilhada
    socket.on(Events.CONVERSATION_MESSAGE, handleNewMessage);

    // Cleanup
    return () => {
      console.log(`🔌 [${hookId}] Removendo listener socket`);
      socket.off(Events.CONVERSATION_MESSAGE, handleNewMessage);

      // Limpar timeout se existir
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [socket, enabled, conversationId, onNewMessage, handleMessageReceived, sendReadReceipt]);

  /**
   * ✅ Listener para confirmação de leitura do backend
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const handleReadReceiptConfirmed = (_data: { conversationId: string; messageIds: string[]; count: number }) => {
      // Invalida contagem de não lidas após confirmação do backend
      queryClient.invalidateQueries({
        queryKey: ['unread-count'],
      });
      queryClient.invalidateQueries({
        queryKey: ['conversations', 'unread-count'],
        exact: false,
      });

      // Invalida lista de conversas para atualizar badges
      queryClient.invalidateQueries({
        queryKey: ['conversations'],
        exact: false, // ← CRÍTICO: invalida infinite queries também
      });
    };

    const handleReadReceiptError = (data: { message: string; error: string }) => {
      console.error('❌ [Frontend] Read receipt error from backend:', data);
    };

    socket.on('read_receipt_confirmed', handleReadReceiptConfirmed);
    socket.on('read_receipt_error', handleReadReceiptError);

    return () => {
      socket.off('read_receipt_confirmed', handleReadReceiptConfirmed);
      socket.off('read_receipt_error', handleReadReceiptError);
    };
  }, [socket, enabled, queryClient]);

  /**
   * ✅ Listener para atualização de status de mensagem (sent, delivered, read, failed)
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const handleMessageStatusUpdate = (data: MessageStatusEvent) => {
      // Filtrar por conversationId se especificado
      if (conversationId && data.conversationId !== conversationId) {
        return;
      }

      // Atualizar status da mensagem no cache
      queryClient.setQueriesData(
        {
          queryKey: ['messages', 'infinite', data.conversationId],
          exact: false
        },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;

          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            items: page.items.map((msg: any) =>
              msg.id === data.messageId
                ? {
                    ...msg,
                    status: data.status,
                    providerMessageId: data.providerMessageId || msg.providerMessageId,
                    // Caminho assíncrono: Meta aceitou e depois reportou falha via webhook
                    // (ex.: janela fechou entre o envio e a entrega). Alimenta a reconciliação
                    // de useMessagingWindow e o CTA "Usar Mensagem Template" do ChatMessage.
                    failedReason: data.failedReason ?? msg.failedReason,
                    failedCode: data.failedCode ?? msg.failedCode,
                    updatedAt: new Date(data.timestamp).toISOString()
                  }
                : msg
            ),
          }));

          return { ...oldData, pages: newPages };
        }
      );
    };

    socket.on(Events.MESSAGE_STATUS, handleMessageStatusUpdate);

    return () => {
      socket.off(Events.MESSAGE_STATUS, handleMessageStatusUpdate);
    };
  }, [socket, enabled, conversationId, queryClient]);

  /**
   * ✅ OPTIMIZATION: Listener para atualização de contadores de mensagens não lidas
   * Backend envia counters após criação de mensagem para evitar HTTP requests
   */
  // NOTE: UNREAD_COUNT_UPDATE listener moved to useUnreadCountSocket (global, in Header)
  // This ensures counters update regardless of which page the user is on.

  /**
   * Listener para reactions em mensagens (webhook WhatsApp ou UI).
   * Atualiza o array reactions[] da mensagem alvo no cache do React Query.
   * Usa string literal 'message:reaction' pois Vite pre-bundle pode cachear
   * versão antiga do SOCKET_EVENTS do @troia-v3/types.
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const handleMessageReaction = (data: MessageReactionEvent) => {
      if (conversationId && data.conversationId !== conversationId) {
        return;
      }

      queryClient.setQueriesData(
        {
          queryKey: ['messages', 'infinite', data.conversationId],
          exact: false
        },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: page.items.map((msg: any) => {
                if (msg.id !== data.messageId) return msg;

                let reactions = [...(msg.reactions || [])];

                // Identificar reactor: por remoteJid (webhook) ou userId (UI)
                const matchesReactor = (r: any) => {
                  if (data.reaction.remoteJid && r.remoteJid) return r.remoteJid === data.reaction.remoteJid;
                  if (data.reaction.userId && r.userId) return r.userId === data.reaction.userId;
                  return false;
                };

                if (data.action === 'remove') {
                  reactions = reactions.filter((r: any) => !matchesReactor(r));
                } else {
                  reactions = reactions.filter((r: any) => !matchesReactor(r));
                  reactions.push(data.reaction);
                }

                return { ...msg, reactions };
              })
            }))
          };
        }
      );
    };

    socket.on('message:reaction', handleMessageReaction);

    return () => {
      socket.off('message:reaction', handleMessageReaction);
    };
  }, [socket, enabled, conversationId, queryClient]);

  /**
   * Listener para edições de mensagens (webhook WhatsApp ou UI).
   * Atualiza content/plainText e marca isEdited=true na mensagem alvo.
   * Usa string literal 'message:edited' pois Vite pre-bundle pode cachear
   * versão antiga do SOCKET_EVENTS do @troia-v3/types.
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const handleMessageEdited = (data: MessageEditedEvent) => {
      if (conversationId && data.conversationId !== conversationId) {
        return;
      }

      queryClient.setQueriesData(
        {
          queryKey: ['messages', 'infinite', data.conversationId],
          exact: false
        },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: page.items.map((msg: any) => {
                if (msg.id !== data.messageId) return msg;
                return {
                  ...msg,
                  content: data.newContent,
                  plainText: data.newPlainText,
                  isEdited: true,
                  editedAt: data.editedAt,
                };
              })
            }))
          };
        }
      );
    };

    socket.on('message:edited', handleMessageEdited);

    return () => {
      socket.off('message:edited', handleMessageEdited);
    };
  }, [socket, enabled, conversationId, queryClient]);

  /**
   * Listener para transcrições STT geradas sob demanda (botão "Transcrever áudio").
   * Atualiza APENAS o plainText da mensagem — não marca como editada.
   * Usa string literal 'message:transcribed' (mesmo motivo do 'message:edited').
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const handleMessageTranscribed = (data: MessageTranscribedEvent) => {
      if (conversationId && data.conversationId !== conversationId) {
        return;
      }

      queryClient.setQueriesData(
        {
          queryKey: ['messages', 'infinite', data.conversationId],
          exact: false
        },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: page.items.map((msg: any) => {
                if (msg.id !== data.messageId) return msg;
                return {
                  ...msg,
                  plainText: data.plainText,
                };
              })
            }))
          };
        }
      );
    };

    socket.on('message:transcribed', handleMessageTranscribed);

    return () => {
      socket.off('message:transcribed', handleMessageTranscribed);
    };
  }, [socket, enabled, conversationId, queryClient]);

  /**
   * Listener para mensagens deletadas (ui local ou webhook do WhatsApp).
   * Remove a mensagem de todas as pages do infinite cache.
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const handleMessageDeleted = (data: MessageDeletedEvent) => {
      if (conversationId && data.conversationId !== conversationId) {
        return;
      }

      queryClient.setQueriesData(
        {
          queryKey: ['messages', 'infinite', data.conversationId],
          exact: false,
        },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: Array.isArray(page?.items)
                ? page.items.filter((msg: any) => msg.id !== data.messageId)
                : page?.items,
            })),
          };
        }
      );

      if (onMessageDelete) {
        onMessageDelete(data.messageId);
      }
    };

    socket.on(Events.MESSAGE_DELETED, handleMessageDeleted);

    return () => {
      socket.off(Events.MESSAGE_DELETED, handleMessageDeleted);
    };
  }, [socket, enabled, conversationId, queryClient, onMessageDelete]);

  /**
   * ✅ Listener para atualização de conversa (status changed, closed, etc.)
   */
  useEffect(() => {
    if (!socket || !enabled) return;

    const handleConversationUpdated = (data: { conversationId: string; updates: { status?: string } }) => {
      console.log('📨 [useChatSocket] conversation:updated recebido:', data);

      // Invalidar lista de conversas para refletir mudança de status
      queryClient.invalidateQueries({
        queryKey: ['conversations'],
        exact: false,
      });

      // Se a conversa atualizada é a atual e foi fechada, invalidar mensagens também
      if (conversationId && data.conversationId === conversationId) {
        queryClient.invalidateQueries({
          queryKey: ['conversation', conversationId],
          exact: false,
        });
      }

      // Callback customizado (se fornecido)
      if (options.onConversationUpdate) {
        options.onConversationUpdate(data.conversationId);
      }
    };

    socket.on(Events.CONVERSATION_UPDATED, handleConversationUpdated);

    return () => {
      socket.off(Events.CONVERSATION_UPDATED, handleConversationUpdated);
    };
  }, [socket, enabled, conversationId, queryClient, options.onConversationUpdate]);

  /**
   * Envia evento de "digitando" para o servidor
   */
  const sendTyping = useCallback((isTyping: boolean) => {
    if (socket?.connected && conversationId) {
      socket.emit('typing', {
        conversationId,
        isTyping,
      });
    }
  }, [socket, conversationId]);

  return {
    /** Se o Socket.IO está conectado */
    isConnected,
    /** Função para enviar evento de digitação */
    sendTyping,
    /** Função para enviar confirmação de leitura */
    sendReadReceipt,
  };
};
